import { DeploymentCenterStackRow, DeploymentCenterStackType } from '@pubstack/common/src/deployment-center'
import { SITE_ENV_LABELS } from '@pubstack/common/src/site'
import { Checkbox } from '@pubstack/ui/components/Checkbox'
import { Chip } from '@pubstack/ui/components/Chip'
import { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Colors } from '~/assets/style/colors'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import Table, { SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Tooltip } from '~/components/Tooltip'
import { PATH_BY_PRODUCT } from '~/hooks/useProduct'
import { STACKS_COMPARATOR_URL } from '~/utils/constants'
import { getEnvFromUrl } from '~/utils/environment'
import { liveRolloutLabel, liveRolloutOf } from './deploymentCenterRollout'
import { DeploymentSelection, headerCheckboxState, isRowSelected } from './deploymentCenterSelection'

/**
 * The product a stack belongs to, in the words the app puts on it. The code namespace stays `adm`/`pbs`
 * everywhere else — it is the API prefix and the tracking prefix — but a column header is read by a
 * customer, who knows the two products as One and Node.
 */
export const STACK_TYPE_LABEL: Record<DeploymentCenterStackType, string> = { adm: 'One', pbs: 'Node' }

export type StackSortBy = 'mediaName' | 'stackName' | 'latestVersion'

/** The media's own page, under the product its stack is typed for. */
const mediaPath = (row: DeploymentCenterStackRow): string => `${PATH_BY_PRODUCT[row.type]}/sites/${row.siteId}`

/**
 * The media's Stacks tab — where a stack is edited, rather than merely read as the flyout reads it.
 *
 * Per-product like `mediaPath`, since this page's URL is product-neutral and would answer One for every row.
 */
const stackPath = (row: DeploymentCenterStackRow): string => `${mediaPath(row)}/stacks`

/**
 * The internal side-by-side of what a stack group serves against what it would serve.
 *
 * `preview` and `local` both point at the `dev` deployment, as the old page did — the comparator has no
 * environment of its own for either.
 */
const comparatorUrl = (row: DeploymentCenterStackRow, scopeId: string): string => {
  const env = getEnvFromUrl(window.location.host)
  return `${STACKS_COMPARATOR_URL}/?scope=${scopeId}&site=${row.siteId}&ref=${row.stackGroupId}&env=${env === 'preview' || env === 'local' ? 'dev' : env}`
}

type Props = {
  rows: DeploymentCenterStackRow[]
  isLoading: boolean
  selection: DeploymentSelection
  scopeId: string
  /** Staff only: the comparator is internal tooling, on a URL a customer has no access to. */
  isPubstackUser: boolean
  sortBy: StackSortBy
  sortOrder: 'ascending' | 'descending'
  onToggleRow: (stackGroupId: string) => void
  onTogglePage: () => void
  onOpenStack: (row: DeploymentCenterStackRow) => void
  onSortChange: (sortBy: StackSortBy, sortOrder: 'ascending' | 'descending') => void
}

/**
 * One row per **stack group**, which is the unit every action on this page operates on.
 *
 * The old page rowed by media and hid the stacks one expand-click down, even though selection was already
 * keyed on `stackGroupId` — so the checkbox you ticked and the thing you acted on were different objects,
 * and a media's tri-state had to be derived back out of the stack selection. Rowing by the group deletes
 * that derivation: the row identity *is* the selection identity.
 *
 * Clicking a row opens the stack itself. There is deliberately no inline expansion of the versions: the
 * flyout already shows the group in full, against what it is currently serving, and an expandable list of
 * version numbers was a second and much poorer view of the same thing. What the expansion was really
 * being read for — whether a rollout is half-finished — is now a column.
 */
export const DeploymentCenterStackTable: FunctionComponent<Props> = ({
  rows,
  isLoading,
  selection,
  scopeId,
  isPubstackUser,
  sortBy,
  sortOrder,
  onToggleRow,
  onTogglePage,
  onOpenStack,
  onSortChange,
}) => {
  const navigate = useNavigate()

  // Every listed row is selectable: both actions act on every row a queue holds, so the header checkbox
  // covers the page whole and nothing has to be held back out of the count.
  const pageIds = rows.map((row) => row.stackGroupId)
  const header = headerCheckboxState(selection, pageIds)

  // Derived from the URL state rather than mirrored into component state: the sort lives in the query
  // string, and a second copy of it here is a second thing that can disagree with the rows on screen.
  const orderOf = (column: StackSortBy) => (sortBy === column ? sortOrder : 'none')

  const columns: TableColumns<DeploymentCenterStackRow> = [
    {
      name: (
        <Checkbox
          checked={header.checked}
          indeterminate={header.indeterminate}
          disabled={rows.length === 0}
          onChange={onTogglePage}
          trackingLabel={header.checked ? 'common-deployment-center-select-page-unset' : 'common-deployment-center-select-page-set'}
        />
      ),
      isSortable: false,
      width: '48px',
    },
    // What the row IS, left to right, before what is happening to it. Media then Stack because a stack is
    // named per media and repeats across them ("Default" on all of them), so the media is what tells two
    // rows apart — and it is the default sort, so it leads the table.
    //
    // The two name columns are the only ones with no width: their content is free text, so there is no
    // width that fits it, and under fixed layout they split whatever the enumerable columns leave.
    { name: 'Media', isSortable: true, order: orderOf('mediaName'), attributeSort: 'mediaName' },
    { name: 'Stack', isSortable: true, order: orderOf('stackName'), attributeSort: 'stackName' },
    // Matches the word the media list prints over One / Node: a column header is read by a customer, who
    // should not meet two names for one thing on two pages.
    { name: 'Product', isSortable: false, width: '90px' },
    // A column of its own rather than a parenthesis after the media name. With Product beside it, what a row
    // is reads as columns, and the name is left to do nothing but identify the media.
    { name: 'Env', isSortable: false, width: '90px' },
    // Then what is happening to it: the state, the version that state is about, and what that leaves on
    // air. Status leads the three because it is the column the tabs are cut by.
    //
    // The widest of the fixed columns, and it has to be: it holds an icon, the longer of two labels, and
    // — for staff — the comparator link after them.
    { name: 'Status', isSortable: false, width: '220px' },
    { name: 'Version', isSortable: true, order: orderOf('latestVersion'), attributeSort: 'latestVersion', width: '110px' },
    // What is being served right now, which is not what the other two answer: they describe pending work,
    // and this says whether a deployment is already under way on the same group.
    { name: 'Live traffic', isSortable: false, width: '170px' },
  ]

  return (
    <Table
      columns={columns}
      isLoading={isLoading}
      // Under fixed layout a header no longer widens its column, so "Live traffic" and the sortable
      // headers — a name plus an 18px sort icon — would wrap onto two lines inside the width they are given.
      noWrapHeader
      onClickHeading={(column) => {
        const clicked = (column as SortableColumn<DeploymentCenterStackRow>).attributeSort as StackSortBy
        onSortChange(clicked, sortBy === clicked && sortOrder === 'ascending' ? 'descending' : 'ascending')
      }}
    >
      {rows.length === 0 ? (
        <TableRow>
          <TableCell colspan={columns.length}>No stacks in this queue.</TableCell>
        </TableRow>
      ) : (
        rows.map((row) => {
          const selected = isRowSelected(selection, row.stackGroupId)
          const rollout = liveRolloutOf(row.versions)

          return (
            <TableRow key={row.stackGroupId} clickable={{ onClick: () => onOpenStack(row), trackingLabel: 'common-deployment-center-stack-view' }}>
              <TableCell>
                {/* Ticking a row must not also open it. */}
                <div onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    checked={selected}
                    onChange={() => onToggleRow(row.stackGroupId)}
                    trackingLabel={selected ? 'common-deployment-center-select-stack-unset' : 'common-deployment-center-select-stack-set'}
                  />
                </div>
              </TableCell>
              {/* Plain text. The row already leads somewhere — clicking it opens the stack — and the one
                  link in it points at that stack on its media, which is where the media name would have
                  gone anyway. A second link a few pixels away, to a page one click further out, only made
                  the row ambiguous about what clicking it does.

                  Bold, spelled out here rather than inherited: `Table` gives the weight to a row's *first*
                  cell, which on a selectable table is the checkbox. The media is what the reader scans this
                  list by — as on the medias list, where it happens to be first and gets it for free. */}
              <TableCell className={'font-bold break-words'}>{row.mediaName}</TableCell>
              <TableCell>
                <div className={'flex items-center gap-2'}>
                  {/* `min-w-0`, because a bare text node in a flex row has `min-width: auto` and so refuses
                      to shrink below its own content — under a fixed column width it would push the link
                      out of the cell rather than wrap. */}
                  <span className={'min-w-0 break-words'}>{row.stackName}</span>
                  {/* Not a refusal — an A/B group is listed, tickable and deployable like any other. It is
                      marked because the deploy does something different to it: only its newest live version
                      is replaced, and the mark is what puts that in view before the row is ticked rather
                      than only in the confirmation after it.

                      Spelled out rather than drawn. A glyph would have to compete with the two the row
                      already carries, and "A/B" is the word the customer uses for this. */}
                  {row.isAbDeployed && (
                    <Tooltip
                      className={'shrink-0'}
                      title={'More than one version is live on this stack. Deploying replaces the newest of them and takes over its share of the traffic; the others keep serving their own.'}
                      reposition
                    >
                      <Chip text={'A/B'} variant={'small'} color={Colors.WarningSoft} borderColor={Colors.Warning} />
                    </Tooltip>
                  )}
                  {/* Only the link swallows the click. The name itself still opens the row, which is what
                      a reader aiming at "the stack" expects to hit, and the flyout is where the stack is
                      read; the link is the way out to where it is *edited*, along with the rest of that
                      media's groups and their history.

                      Three guards, all of them load-bearing: a real `href` so middle-click and "open in
                      new tab" work, `preventDefault` so an ordinary click routes instead of reloading the
                      app, and the `stopPropagation` above so it does not also fire the row. */}
                  <div onClick={(event) => event.stopPropagation()}>
                    <Tooltip title={'Open this stack on its media'} reposition>
                      <Link
                        icon={'open_in_new'}
                        iconSize={'16px'}
                        label={''}
                        href={stackPath(row)}
                        onClick={(event) => {
                          event.preventDefault()
                          navigate(stackPath(row))
                        }}
                        trackingLabel={'common-deployment-center-stack-navigate'}
                      />
                    </Tooltip>
                  </div>
                </div>
              </TableCell>
              <TableCell>{STACK_TYPE_LABEL[row.type]}</TableCell>
              <TableCell>{SITE_ENV_LABELS[row.mediaEnv]}</TableCell>
              <TableCell>
                <div className={'flex items-center gap-1'}>
                  <Icon name={row.state === 'to-deploy' ? 'send' : 'sync_problem'} width={'18px'} height={'18px'} fill={row.state === 'to-deploy' ? Colors.Success : Colors.Warning} />
                  <span className={'whitespace-nowrap'}>{row.state === 'to-deploy' ? 'Ready to deploy' : 'Out of sync'}</span>
                  {/* The comparator sits with the status because it answers the question the status raises
                      — what this group would serve, against what it serves — and not the one the stack's
                      name raises, which is "where do I go to change this?".

                      Staff only: internal tooling on a URL a customer has no access to, so a customer sees
                      the status text alone and the column simply does not grow a link. */}
                  {isPubstackUser && (
                    <div className={'ml-1'} onClick={(event) => event.stopPropagation()}>
                      <Tooltip title={'Compare this stack in the stacks comparator'} reposition>
                        <Link
                          icon={'open_in_new'}
                          iconSize={'16px'}
                          label={''}
                          href={comparatorUrl(row, scopeId)}
                          target={'_blank'}
                          rel={'noreferrer'}
                          trackingLabel={'common-stacks-comparator-navigate'}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>v{row.latestVersion}</TableCell>
              <TableCell>
                {/* An unfinished rollout has to be distinguishable at a glance — that is the column's whole
                    reason to exist — but the text already says it, so the colour carries it rather than an
                    icon competing with the one Status already puts on the row. */}
                {rollout.kind === 'partial' ? (
                  <Tooltip title={'A deployment is in progress on this stack — traffic is split between versions.'} reposition>
                    <span className={'font-bold text-warning-ink'}>{liveRolloutLabel(rollout, row.latestVersion)}</span>
                  </Tooltip>
                ) : (
                  <span className={rollout.kind === 'none' ? 'text-slate-grey' : undefined}>{liveRolloutLabel(rollout, row.latestVersion)}</span>
                )}
              </TableCell>
            </TableRow>
          )
        })
      )}
    </Table>
  )
}
